import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bookmark, Check, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import Dialog from "@/components/atoms/Dialog";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { scheduleDeferredDeleteWithUndo } from "@/lib/deferredDeleteWithUndo";
import {
  savedFiltersService,
  type SavedFilter,
  type SavedFilterConfig,
} from "@/services/savedFiltersService";
import {
  usePageScope,
  useSettingsStore,
  type ScopePageKey,
} from "@/store/settingsStore";

interface Props {
  pageName: string;
  filterParamKeys?: readonly string[];
  hasActiveFilterParams?: (params: URLSearchParams) => boolean;
  scopePage?: ScopePageKey;
  captureFilterState?: () => Record<string, unknown>;
  applyFilterState?: (state: Record<string, unknown>) => void;
}

const NO_FILTER_PARAM_KEYS: readonly string[] = [];

function selectParams(current: URLSearchParams, keys: ReadonlySet<string>): string {
  const selected = new URLSearchParams();
  current.forEach((value, key) => {
    if (keys.has(key)) selected.append(key, value);
  });
  selected.sort();
  return selected.toString();
}

function hasSelectedParamValue(current: URLSearchParams, keys: ReadonlySet<string>): boolean {
  let hasValue = false;
  current.forEach((value, key) => {
    if (keys.has(key) && value.trim()) hasValue = true;
  });
  return hasValue;
}

function hasFilterValue(value: unknown): boolean {
  if (value == null || value === "" || value === false) return false;
  if (Array.isArray(value)) return value.some(hasFilterValue);
  if (typeof value === "object") return Object.values(value).some(hasFilterValue);
  return true;
}

/** Separate controls for creating a saved view and reopening the user's saved views. */
export function SavedFiltersButton({
  pageName,
  filterParamKeys = NO_FILTER_PARAM_KEYS,
  hasActiveFilterParams,
  scopePage,
  captureFilterState,
  applyFilterState,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedKeys = useMemo(() => new Set(filterParamKeys), [filterParamKeys]);
  const scope = usePageScope(scopePage ?? "contacts");
  const setScope = useSettingsStore((state) => state.setScope);
  const scopeLabels = useSettingsStore((state) => state.scopeLabels);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [items, setItems] = useState<SavedFilter[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingDeleteIds = useRef(new Set<string>());
  const selectedQuery = selectParams(searchParams, allowedKeys);
  const capturedState = captureFilterState?.() ?? {};
  const hasAppliedFilters = Boolean(
    (hasActiveFilterParams
      ? hasActiveFilterParams(searchParams)
      : hasSelectedParamValue(searchParams, allowedKeys))
      || (scopePage && (scope.legalEntityIds.length > 0 || scope.branchIds.length > 0))
      || hasFilterValue(capturedState),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await savedFiltersService.list(pageName);
      setItems(loaded.filter((item) => !pendingDeleteIds.current.has(item.id)));
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "Saved filters could not be loaded.");
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [pageName]);

  useEffect(() => {
    void load();
  }, [load]);

  const currentConfig = useCallback((): SavedFilterConfig => ({
    query: selectedQuery,
    ...(captureFilterState ? { state: captureFilterState() } : {}),
    ...(scopePage
      ? {
          scope: {
            legalEntityIds: Array.isArray(scope?.legalEntityIds) ? [...scope.legalEntityIds] : [],
            branchIds: Array.isArray(scope?.branchIds) ? [...scope.branchIds] : [],
          },
        }
      : {}),
  }), [captureFilterState, scope, scopePage, selectedQuery]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      const created = await savedFiltersService.create({
        name: trimmed,
        pageName,
        filterConfig: currentConfig(),
      });
      setItems((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
      setName("");
      setSaveOpen(false);
      toast.success(`Saved "${created.name}".`);
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "The filter could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    void save().catch((error) => {
      toast.error(getApiErrorMessage(error) ?? "The filter could not be saved.");
    });
  };

  const closeSaveDialog = () => {
    setName("");
    setSaveOpen(false);
  };

  const apply = (saved: SavedFilter) => {
    const next = new URLSearchParams(searchParams);
    for (const key of allowedKeys) next.delete(key);
    const savedParams = new URLSearchParams(saved.filterConfig?.query ?? "");
    savedParams.forEach((value, key) => {
      if (allowedKeys.has(key)) next.append(key, value);
    });
    if (scopePage && saved.filterConfig?.scope) {
      const savedScope = saved.filterConfig.scope;
      const legalEntityIds = Array.isArray(savedScope.legalEntityIds) ? savedScope.legalEntityIds : [];
      const branchIds = Array.isArray(savedScope.branchIds) ? savedScope.branchIds : [];
      const selectedLabels: Record<string, string> = {};
      for (const id of [...legalEntityIds, ...branchIds]) {
        if (scopeLabels[id]) selectedLabels[id] = scopeLabels[id];
      }
      setScope(scopePage, { legalEntityIds, branchIds }, selectedLabels);
    }
    applyFilterState?.(saved.filterConfig?.state ?? {});
    if (allowedKeys.size > 0) setSearchParams(next, { replace: true });
    setLibraryOpen(false);
    toast.success(`Applied "${saved.name}".`);
  };

  const remove = (saved: SavedFilter) => {
    const restore = () => {
      setItems((current) => {
        if (current.some((item) => item.id === saved.id)) return current;
        return [...current, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
    };

    scheduleDeferredDeleteWithUndo({
      idKey: `saved-filter:${saved.id}`,
      label: saved.name,
      hint: "This saved filter will be permanently deleted.",
      onScheduled: () => {
        pendingDeleteIds.current.add(saved.id);
        setItems((current) => current.filter((item) => item.id !== saved.id));
      },
      onUndo: () => {
        pendingDeleteIds.current.delete(saved.id);
        restore();
      },
      onFailed: () => {
        pendingDeleteIds.current.delete(saved.id);
        restore();
      },
      onCommit: () => savedFiltersService.delete(saved.id),
      onComplete: () => {
        pendingDeleteIds.current.delete(saved.id);
        setItems((current) => current.filter((item) => item.id !== saved.id));
        toast.success(`Deleted "${saved.name}".`);
      },
    });
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => setLibraryOpen(true)}>
          <Bookmark size={14} /> Saved filters
          <span
            className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-bridge-accent/10 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-bridge-accent"
            aria-label={loaded
              ? `${items.length} saved ${items.length === 1 ? "filter" : "filters"}`
              : "Loading saved filters"}
          >
            {loaded ? items.length : "…"}
          </span>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!hasAppliedFilters}
          title={hasAppliedFilters ? "Save the current filters" : "Apply at least one filter to save it"}
          onClick={() => setSaveOpen(true)}
        >
          <Save size={14} /> Save Filter
        </Button>
      </div>

      <Dialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        title="Saved filters"
        subtitle="Choose a saved view to apply to this page."
        cancelLabel="Close"
        showSubmit={false}
      >
        <div>
          <h3 className="mb-2 text-sm font-semibold text-bridge-fg">Your saved views</h3>
          {loading ? (
            <div className="flex items-center gap-2 py-5 text-sm text-bridge-muted">
              <Loader2 size={16} className="animate-spin" /> Loading saved filters...
            </div>
          ) : items.length === 0 ? (
            <p className="rounded-md border border-dashed border-bridge-border p-4 text-sm text-bridge-muted">
              You have no saved filters for this page yet.
            </p>
          ) : (
            <ul className="divide-y divide-bridge-border/60 rounded-md border border-bridge-border/70">
              {items.map((saved) => (
                <li key={saved.id} className="flex items-center gap-3 px-3 py-2.5">
                  <Bookmark size={15} className="shrink-0 text-bridge-accent" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-bridge-fg">{saved.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => apply(saved)}>
                    <Check size={14} /> Apply
                  </Button>
                  <button
                    type="button"
                    aria-label={`Delete ${saved.name}`}
                    onClick={() => remove(saved)}
                    className="rounded-md p-2 text-bridge-muted hover:bg-red-50 hover:text-bridge-danger"
                  >
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Dialog>

      <Dialog
        open={saveOpen}
        onClose={closeSaveDialog}
        title="Save Filter"
        subtitle="Give this set of filters a name so you can use it again later."
        submitLabel={saving ? "Saving..." : "Save Filter"}
        submitClassName="bg-bridge-accent hover:bg-bridge-accentSoft"
        submitDisabled={!name.trim() || saving}
        onSubmit={handleSave}
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-bridge-accent/20 bg-bridge-accent/5 px-4 py-3">
            <p className="text-sm font-medium text-bridge-fg">What will be saved</p>
            <p className="mt-1 text-xs leading-5 text-bridge-muted">
              Your current filters and entity or branch selection. Sorting and page number are not included.
            </p>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-bridge-fg" htmlFor={`${pageName}-filter-name`}>
              Filter name
            </label>
            <Input
              id={`${pageName}-filter-name`}
              value={name}
              maxLength={100}
              autoFocus
              placeholder="e.g. Sales properties in Leeds"
              leading={<Save size={15} />}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSave();
                }
              }}
            />
            <p className="mt-1.5 text-xs text-bridge-muted">
              This saved filter will appear under Saved filters and is visible only to you.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
}
